import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import HeroSection from '@/components/HeroSection';
import ProductCard from '@/components/ProductCard';
import CategoryCard from '@/components/CategoryCard';
import SafeImage from '@/components/SafeImage';
import { useCategories, useHomepageProducts } from '@/hooks/use-products';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Heart, Star, Phone, Mail, MapPin, Send, Coffee } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useEffect, useRef, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import Seo from '@/components/Seo';
import { useShopPause } from '@/hooks/use-shop-pause';

const Index = () => {
  const { data: categories = [] } = useCategories();
  const { data: featuredProducts = [] } = useHomepageProducts();
  const { data: shopPause } = useShopPause();
  const shopClosed = !!shopPause?.paused;
  const { toast } = useToast();
  const [contactLoading, setContactLoading] = useState(false);
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  const [consentChecked, setConsentChecked] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  const mapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mapVisible || !mapRef.current) return;
    const el = mapRef.current;
    if (typeof IntersectionObserver === 'undefined') {
      setMapVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMapVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mapVisible]);

  const mainCategories = categories.filter((c) => !c.parent_id);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const name = (formData.get('name') as string)?.trim();
    const email = (formData.get('email') as string)?.trim();
    const message = (formData.get('message') as string)?.trim();
    const errs: Record<string, string> = {};
    if (!name || name.length < 2) errs.name = 'Mind. 2 Zeichen.';
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Ungültige E-Mail.';
    if (!message || message.length < 10) errs.message = 'Mind. 10 Zeichen.';
    if (!consentChecked) errs.consent = 'Bitte stimmen Sie der Datenverarbeitung zu.';
    setContactErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setContactLoading(true);
    try {
      const id = crypto.randomUUID();
      const { error } = await supabase.from('contact_inquiries').insert({ id, name, email, message });
      if (error) throw error;
      // Send confirmation email to customer
      supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'contact-confirmation',
          recipientEmail: email,
          referenceId: id,
          referenceType: 'inquiry',
          idempotencyKey: `contact-confirm-${id}`,
          templateData: { customerName: name, message },
        },
      }).catch(() => {});
      // Notify admin
      supabase.functions.invoke('notify-contact', { body: { name, email, message } }).catch(() => {});
      toast({ title: 'Nachricht gesendet ✉️', description: 'Vielen Dank! Wir melden uns bald bei Ihnen.' });
      setContactErrors({});
      setConsentChecked(false);
      (e.target as HTMLFormElement).reset();
    } catch {
      toast({ title: 'Fehler beim Senden', description: 'Bitte versuchen Sie es erneut.', variant: 'destructive' });
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title="cycy art – Handgemachte Kunst & Gravuren"
        description="Handgefertigte Kunstwerke, individueller Modeschmuck und personalisierte Gravuren aus Düsseldorf."
        path="/"
      />
      <Navbar />

      <main id="main-content" tabIndex={-1} className="flex-1">
        <HeroSection />

        {/* Temporary closure notice */}
        {shopClosed && (
          <section
            className="border-b border-border/60"
            style={{ background: 'hsl(var(--gold) / 0.1)' }}
            role="status"
          >
            <div className="container py-3 sm:py-4 flex items-center justify-center gap-3 text-center">
              <div className="hidden sm:flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-card text-[hsl(var(--gold))]">
                <Coffee className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="space-y-0.5">
                <p className="font-heading text-sm sm:text-base font-semibold text-foreground leading-tight">
                  Vorübergehend geschlossen
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground leading-snug max-w-xl">
                  Bestellungen sind aktuell nicht möglich – du kannst dich gerne umsehen und inspirieren lassen.
                </p>
              </div>
            </div>
          </section>
        )}


        {/* Stats */}
        <section className="border-y border-border bg-[#f9f9a4]/80 dark:bg-gold/15">
          <div className="container py-3 sm:py-5 md:py-8">
            <div className="grid grid-cols-3 gap-2 sm:gap-6 md:gap-10 text-center">
              {[
                { icon: Heart, number: '100+', label: 'Zufriedene Kunden' },
                { icon: Star, number: '300+', label: 'Produkte verkauft' },
                { icon: Leaf, number: '200', label: 'Gravuren durchgeführt' },
              ].map((stat) => (
                <div key={stat.label} className="space-y-1 sm:space-y-1.5">
                  <div className="mx-auto inline-flex h-8 w-8 sm:h-11 sm:w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <stat.icon className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
                  </div>
                  <p className="font-heading text-lg sm:text-2xl md:text-3xl font-bold text-foreground leading-tight">{stat.number}</p>
                  <p className="text-[10px] sm:text-sm text-foreground/70 leading-tight">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="container section-y">
          <div className="flex items-end justify-between mb-4 md:mb-7 gap-3">
            <div className="space-y-1.5 sm:space-y-2">
              <span className="eyebrow">Kategorien</span>
              <h2 className="heading-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold">Unsere Kollektionen</h2>
            </div>
            <Link to="/shop" className="shrink-0 inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-primary hover:gap-2 transition-all">
              Alle ansehen <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
            {mainCategories.map((cat, i) => (
              <CategoryCard key={cat.id} category={cat} priority={i === 0} />
            ))}
          </div>
        </section>

        {/* Featured Products */}
        {featuredProducts.length > 0 && (() => {
          // Show counts that always fill rows on every breakpoint:
          // mobile 2 cols, sm 3 cols, lg 4 cols → max 8, but trim to a multiple of 4 if possible, else 6, else 4.
          const list = [...featuredProducts]
            .sort((a, b) => {
              // Prioritize "angebot" badge first for visual sortierung
              const ab = a.badge === 'angebot' ? 0 : 1;
              const bb = b.badge === 'angebot' ? 0 : 1;
              if (ab !== bb) return ab - bb;
              return 0;
            });
          const count = list.length >= 8 ? 8 : list.length >= 6 ? 6 : list.length >= 4 ? 4 : list.length;
          const visible = list.slice(0, count);
          return (
            <section className="bg-card section-y">
              <div className="container">
                <div className="flex items-end justify-between mb-4 md:mb-7 gap-3">
                  <div className="space-y-1.5 sm:space-y-2">
                    <span className="eyebrow">Empfohlen</span>
                    <h2 className="heading-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold">Beliebte Produkte</h2>
                    <p className="text-sm text-muted-foreground max-w-md hidden sm:block">
                      Eine handverlesene Auswahl unserer Lieblingsstücke und aktuellen Angebote.
                    </p>
                  </div>
                  <Link to="/shop" className="shrink-0 inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-primary hover:gap-2 transition-all">
                    Zum Shop <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5 md:gap-6">
                  {visible.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
                <div className="mt-6 sm:mt-10 flex justify-center">
                  <Button asChild variant="outline" size="lg" className="h-11 sm:h-12 px-5 sm:px-6 w-full sm:w-auto">
                    <Link to="/shop">
                      Alle Produkte ansehen <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </section>
          );
        })()}

        {/* About Teaser */}
        <section className="container section-y">
          <div className="grid md:grid-cols-2 gap-6 md:gap-14 items-center">
            <div className="space-y-3 md:space-y-5 order-2 md:order-1">
              <span className="eyebrow">Über cycy</span>
              <h2 className="heading-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight text-balance">
                Mit einem Blatt – eine Emotion eingravieren
              </h2>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                Bei cycy vereinen wir unsere Leidenschaft für Kunst mit dem Ziel, besondere Momente in einzigartigen Gravuren und sorgfältig ausgewählten Geschenken festzuhalten.
              </p>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed hidden sm:block">
                Jedes Blatt wird sorgfältig ausgewählt und durch präzise Gravurtechniken werden Fotos, Namen, Gedichte oder ein zärtliches „Ich liebe dich" darauf verewigt.
              </p>
              <Button asChild variant="outline" className="mt-1 sm:mt-2 w-full sm:w-auto h-11">
                <Link to="/ueber-uns">
                  Mehr erfahren <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="relative order-1 md:order-2">
              <div className="absolute -inset-3 sm:-inset-4 rounded-3xl bg-gold/15 blur-2xl" aria-hidden="true" />
              <SafeImage
                src="https://ymrzpmvwuxphomnuzgvp.supabase.co/storage/v1/object/public/gallery/31be9752-d5d6-42f7-8fb5-d779e2a2e29d/w1600.jpg"
                alt="cycy Kunstwerk"
                loading="lazy"
                decoding="async"
                wrapperClassName="relative rounded-2xl shadow-[var(--shadow-elegant)] w-full aspect-[4/3]"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          className="text-primary-foreground section-y relative overflow-hidden"
          style={{ background: 'var(--gradient-warm)' }}
        >
          <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-gold/20 blur-3xl" aria-hidden="true" />
          <div className="container relative text-center space-y-3 sm:space-y-5">
            <h2 className="heading-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold">Kostenloser Versand ab 59€</h2>
            <p className="text-sm md:text-base text-primary-foreground/85 max-w-md mx-auto">
              Entdecke unsere handgefertigten Kunstwerke und personalisierten Gravuren.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-foreground px-6 sm:px-7 h-11 sm:min-h-12 text-sm font-semibold text-primary hover:bg-primary-foreground/90 hover:gap-3 transition-all shadow-[var(--shadow-elegant)] w-full sm:w-auto max-w-xs mx-auto"
            >
              Zum Shop <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* Contact Section */}
        <section id="kontakt" className="container pt-7 sm:pt-10 md:pt-14 pb-0 scroll-mt-20">
          <div className="text-center mb-5 md:mb-8 space-y-1.5 sm:space-y-2">
            <span className="eyebrow">Kontakt</span>
            <h2 className="heading-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold">Kontakt aufnehmen</h2>
            <p className="text-sm md:text-base text-muted-foreground pt-1">Wir freuen uns auf deine Nachricht.</p>
          </div>
          <div className="grid md:grid-cols-5 gap-6 md:gap-12 max-w-4xl mx-auto md:items-stretch">
            <form onSubmit={handleContactSubmit} className="md:col-span-3 flex flex-col space-y-4">
              <div className="bg-card rounded-2xl border border-border p-4 sm:p-5 md:p-8 flex-1 flex flex-col space-y-4">
                <div>
                  <label htmlFor="contact-name" className="text-sm font-medium mb-1.5 block">Name *</label>
                  <Input id="contact-name" name="name" required placeholder="Dein vollständiger Name" maxLength={100} autoComplete="name" />
                  {contactErrors.name && <p className="text-xs text-destructive mt-1">{contactErrors.name}</p>}
                </div>
                <div>
                  <label htmlFor="contact-email" className="text-sm font-medium mb-1.5 block">E-Mail *</label>
                  <Input id="contact-email" name="email" required type="email" placeholder="deine@email.de" maxLength={255} autoComplete="email" inputMode="email" />
                  {contactErrors.email && <p className="text-xs text-destructive mt-1">{contactErrors.email}</p>}
                </div>
                <div className="flex-1 flex flex-col">
                  <label htmlFor="contact-message" className="text-sm font-medium mb-1.5 block">Nachricht *</label>
                  <Textarea id="contact-message" name="message" required placeholder="Wie können wir dir helfen?" className="flex-1 min-h-[80px]" maxLength={2000} />
                  {contactErrors.message && <p className="text-xs text-destructive mt-1">{contactErrors.message}</p>}
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="consent"
                    checked={consentChecked}
                    onCheckedChange={(checked) => setConsentChecked(checked === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="consent" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                    Ich bin damit einverstanden, dass meine Daten zum Zwecke der Kontaktaufnahme gespeichert und verarbeitet werden. Mir ist bekannt, dass ich meine Einwilligung jederzeit widerrufen kann.*
                  </label>
                </div>
                {contactErrors.consent && <p className="text-xs text-destructive">{contactErrors.consent}</p>}
                <p className="text-xs text-muted-foreground">*Bitte fülle alle erforderlichen Felder aus.</p>
                <Button type="submit" disabled={contactLoading} className="w-full font-body h-11">
                  {contactLoading ? 'Wird gesendet...' : <><Send className="mr-2 h-4 w-4" /> Nachricht senden</>}
                </Button>
              </div>
            </form>
            <div className="md:col-span-2 space-y-5">
              <div className="bg-card rounded-2xl border border-border p-5 md:p-6 space-y-5">
                <h3 className="font-heading font-semibold text-base">Kontaktdaten</h3>
                <a href="tel:+4917657861376" className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors">
                  <div className="p-2 rounded-lg bg-primary/10 shrink-0"><Phone className="h-4 w-4 text-primary" /></div>
                  <div><span className="block">+49 176 578 613 76</span><span className="text-xs">(auch auf WhatsApp)</span></div>
                </a>
                <a href="mailto:cycyart1314@gmail.com" className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors">
                  <div className="p-2 rounded-lg bg-primary/10 shrink-0"><Mail className="h-4 w-4 text-primary" /></div>
                  cycyart1314@gmail.com
                </a>
                <a href="https://www.google.com/maps/search/?api=1&query=Westfalenstra%C3%9Fe+55,+40472+D%C3%BCsseldorf" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-sm text-muted-foreground hover:text-primary transition-colors">
                  <div className="p-2 rounded-lg bg-primary/10 shrink-0 mt-0.5"><MapPin className="h-4 w-4 text-primary" /></div>
                  <span>Westfalenstraße 55,<br />40472 Düsseldorf</span>
                </a>
              </div>
              <div ref={mapRef} className="rounded-2xl overflow-hidden border border-border h-56 sm:h-72 md:h-96 bg-muted">
                {mapVisible && (
                  <iframe
                    title="Standort"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2496.623466142756!2d6.8163744!3d51.2628418!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8c9974d40a809%3A0xfe5579a7d04d74e!2scycy%20Geschenk%20Onlineshop!5e0!3m2!1sde!2sde!4v1775040388179!5m2!1sde!2sde"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                    className="w-full h-full"
                    loading="lazy"
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
